import {CodeViewHeader} from '@github-ui/code-view-header'
import type {HeaderResponse} from '@github-ui/code-view-header/types'
import type {LayoutPayload} from '@github-ui/code-view-types'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery} from '@github-ui/react-query'
import {SplitPageLayout} from '@primer/react'

import {SharedPageLayout} from '../components/SharedPageLayout'
import {CreatingState} from '../components/states/empty/CreatingState'
import {Nothing} from '../components/states/empty/Nothing'
import {OnboardingPrompts} from '../components/states/empty/OnboardingPrompts'
import {QuickSetupBox} from '../components/states/empty/QuickSetupBox'

// Header fallback used while the real `/_sidebar/about` response is still in
// flight. Keeps the empty-repo page interactive (clone box, prompts) without
// blocking on a request that only fills in metadata-heavy header sections.
function buildFallbackHeaderData(data: LayoutPayload): HeaderResponse {
  const {repo, currentUser, repoState} = data
  const emptyState = repoState?.state === 'empty' ? repoState.data : undefined

  return {
    ownerLogin: repo.ownerLogin,
    repoName: repo.name,
    isOrg: repo.isOrgOwned,
    topics: [],
    showInsights: false,
    stargazerCount: 0,
    watcherCount: 0,
    forksCount: 0,
    stargazersPath: '',
    watchersPath: '',
    forkNetworkPath: '',
    canEditMetadata: false,
    sections: {
      releases: false,
      sponsors: false,
      deployments: false,
      packages: false,
      usedBy: false,
      contributors: false,
      languages: false,
      cta: false,
      suggestedWorkflows: false,
    },
    repo: {
      ownerAvatarUrl: repo.ownerAvatar,
      isPrivate: repo.private,
      isTemplate: false,
      isFork: repo.isFork,
      isMirror: emptyState?.isMirror ?? false,
      isArchived: false,
      visibilityLabel: repo.private ? 'Private' : 'Public',
    },
    star: {viewerHasStarred: false, canStar: false},
    fork: {canFork: false},
    pin: {canPin: false, isPinned: false, isOrgOwned: repo.isOrgOwned, pinItemsRemaining: 0},
    watch: {
      canWatch: false,
      watchData: {
        repositoryId: `${repo.databaseId ?? ''}`,
        repositoryName: `${repo.ownerLogin}/${repo.name}`,
        watchersCount: 0,
        subscriptionType: 'none',
        subscribableThreadTypes: [],
        showLabelSubscriptions: false,
        subscribedLabels: [],
      },
    },
    viewer: {isLoggedIn: !!currentUser, isSiteAdmin: !!currentUser?.isStaff, emuContributionBlocked: false},
    showSponsorButton: false,
    showTemplateButton: false,
  }
}

export function CodeViewEmptyLayout({data}: {data: LayoutPayload}) {
  const {repo, repoState} = data

  // Fetch the header/sidebar payload alongside the layout so the empty-repo
  // CTAs (clone box, prompts) render immediately. The destructuring default
  // (`headerData = buildFallbackHeaderData(data)`) provides a synchronous
  // fallback built from the layout payload while the real request is in flight;
  // once it resolves, the header re-renders with live data.
  const sidebarAboutQueryDeps = {pathname: `/${repo.ownerLogin}/${repo.name}/_sidebar/about`}
  const {data: headerData = buildFallbackHeaderData(data), isPending} = useQuery({
    queryKey: [repo.ownerLogin, repo.name, 'sidebarAbout', sidebarAboutQueryDeps] as const,
    queryFn: () => queryFnFetch<HeaderResponse>({queryDeps: sidebarAboutQueryDeps}),
  })

  if (repoState?.state !== 'empty') return null
  const {isMirror, onboardingPrompts, ...urls} = repoState.data

  return (
    <SharedPageLayout narrow={false}>
      <SplitPageLayout className="container-xl">
        <SplitPageLayout.Header padding="none" divider="none" className="tmp-px-3">
          <CodeViewHeader key={isPending ? 'fallback' : 'live'} {...headerData} />
        </SplitPageLayout.Header>
        <SplitPageLayout.Content as="div" padding="condensed" width="xlarge">
          {repo.isFork ? (
            <CreatingState title="Forking your repository" description="It should only take a few seconds." />
          ) : isMirror ? (
            <CreatingState
              title="Mirroring your repository"
              description="This may take anywhere from a few minutes to a few hours depending on the size of the repository being mirrored."
            />
          ) : !repo.currentUserCanPush ? (
            <Nothing />
          ) : (
            <>
              <OnboardingPrompts prompts={onboardingPrompts} />
              <QuickSetupBox repo={repo} urls={urls} />
            </>
          )}
        </SplitPageLayout.Content>
      </SplitPageLayout>
    </SharedPageLayout>
  )
}
